import type {
    FairnessStep,
    FaqItem,
    GameId,
    GameInfo,
    PricingTier,
    TitledText
} from '@/types'

export const LINKS = {
    signUp: 'https://app.kinematic.run',
    signIn: 'https://app.kinematic.run',
    lobby: 'https://kinematic.run/games/',
    docs: 'https://docs.kinematic.run',
    quickstart: 'https://docs.kinematic.run/quickstart',
    agents: 'https://docs.kinematic.run/agents',
    provablyFair: 'https://docs.kinematic.run/concepts/provably-fair',
    walletApi: 'https://docs.kinematic.run/reference/wallet-api',
    element: 'https://docs.kinematic.run/reference/element',
    email: 'mailto:hello@kinematic.run',
    legal: 'https://app.kinematic.run/legal'
}

export const CONTACT_EMAIL = 'hello@kinematic.run'

export const demoUrl = (id: GameId): string => `https://kinematic.run/games/?game=${id}`

export const GAMES: Record<GameId, GameInfo> = {
    crash: {
        id: 'crash',
        name: 'Crash',
        description: 'The multiplier climbs until the rocket crashes, and players cash out before it does.',
        spec: 'Auto cash out',
        max: '1,000,000'
    },
    mines: {
        id: 'mines',
        name: 'Mines',
        description: 'Players turn over tiles on a board of 25 and cash out before they find a mine.',
        spec: '1 to 24 mines',
        max: '5,148,297'
    },
    dice: {
        id: 'dice',
        name: 'Dice',
        description: 'Players roll over or under a target they set, and a narrower target pays more.',
        spec: 'Over or under',
        max: '49.74'
    },
    limbo: {
        id: 'limbo',
        name: 'Limbo',
        description: 'Players name a multiplier and win when the result reaches it.',
        spec: 'Any target',
        max: '1,000,000'
    },
    roulette: {
        id: 'roulette',
        name: 'Roulette',
        description: 'Players bet on a European wheel with a single zero and a full chip layout.',
        spec: 'Single zero',
        max: '36'
    },
    keno: {
        id: 'keno',
        name: 'Keno',
        description: 'Players pick up to ten of forty numbers, and the draw pays by how many it hits.',
        spec: '10 of 40',
        max: '1,000'
    },
    plinko: {
        id: 'plinko',
        name: 'Plinko',
        description: 'A ball falls through 8 to 16 rows of pegs into a multiplier at the bottom.',
        spec: '8 to 16 rows',
        max: '1,000'
    },
    pump: {
        id: 'pump',
        name: 'Pump',
        description: 'Each pump raises the multiplier or bursts the bubble, so players cash out in time.',
        spec: 'Four risks',
        max: '1,070,759'
    },
    wheel: {
        id: 'wheel',
        name: 'Wheel',
        description: 'A wheel of 10 to 50 segments spins, and one crown segment pays the most.',
        spec: '10 to 50 segments',
        max: '49'
    },
    diamonds: {
        id: 'diamonds',
        name: 'Diamonds',
        description: 'Five gems land in a row, and matching gems form the pattern that pays.',
        spec: 'Three risks',
        max: '250'
    },
    hilo: {
        id: 'hilo',
        name: 'HiLo',
        description: 'Players call the next card higher, lower or the same, and cash out after any right call.',
        spec: 'Up to 52 skips',
        max: '1,000,000'
    },
    baccarat: {
        id: 'baccarat',
        name: 'Baccarat',
        description: 'Players back the Player, the Banker or a Tie, and fixed rules draw the cards.',
        spec: 'Player, Banker, Tie',
        max: '9'
    },
    blackjack: {
        id: 'blackjack',
        name: 'Blackjack',
        description: 'Players play one hand against the dealer, with doubles, splits and insurance.',
        spec: 'Splits to four hands',
        max: '16'
    }
}

export const PLINKO_8 = [13, 3, 1.3, 0.7, 0.4, 0.7, 1.3, 3, 13] as const
export const PLINKO_12 = [33, 11, 4, 2, 1.1, 0.6, 0.3, 0.6, 1.1, 2, 4, 11, 33] as const
export const PLINKO_16 = [110, 41, 10, 5, 3, 1.5, 1, 0.5, 0.3, 0.5, 1, 1.5, 3, 5, 10, 41, 110] as const

export const RTP_OPTIONS = [99, 98, 97, 96] as const

export const TECH_STACK = [
    'Cloudflare Workers',
    'Durable Objects',
    'Ed25519 signatures',
    'BN254 VRF proofs',
    'SHA-256 seed commitments',
    'WebAssembly verifier',
    'Solana devnet records',
    'USDC, USDT and SOL',
    'Desktop and phone',
    'RTP 99, 98, 97 or 96%'
]

export const SERVER_FEATURES: TitledText[] = [
    {
        title: 'One Worker in your account',
        text: 'The dashboard deploys it into your own Cloudflare account, on workers.dev or your own hostname, with its own keys and limits.'
    },
    {
        title: 'One actor per player',
        text: 'Each player and currency gets its own Durable Object that holds their rounds and wallet calls, so no player waits behind another.'
    },
    {
        title: 'Your wallet keeps the money',
        text: "The server calls three signed endpoints: debit, credit and balance. Every credit carries the round's proof, so your wallet can check it before it pays."
    },
    {
        title: 'Settings without a redeploy',
        text: 'The dashboard pushes your settings to the server and keeps it on the latest release, with no redeploy.'
    }
]

export const FAIRNESS_STEPS: FairnessStep[] = [
    {
        key: 'commit',
        title: 'Commit',
        tech: 'SHA-256',
        text: 'The browser makes a fresh seed for each bet and sends only its hash.',
        sample: '9f3a51c2e07b…d41c0e'
    },
    {
        key: 'sign',
        title: 'Sign',
        tech: 'Ed25519',
        text: 'A device key that never leaves the browser signs the bet and the hash.',
        sample: '4c1e9b7f20aa…7b20f3'
    },
    {
        key: 'accept',
        title: 'Accept',
        tech: 'Ed25519',
        text: 'Your game server debits the wallet and signs its acceptance before the seed is revealed.',
        sample: 'b81d02f6c39e…e95a17'
    },
    {
        key: 'decide',
        title: 'Decide',
        tech: 'BN254 VRF',
        text: "Your server's own VRF key gives exactly one valid proof, and that proof decides the outcome.",
        sample: 'π 2f8c07ab…19de44'
    },
    {
        key: 'verify',
        title: 'Verify',
        tech: 'WebAssembly',
        text: 'The browser checks the signatures, the seed and the proof, then recomputes the result.',
        sample: '4 of 4 checks passed'
    },
    {
        key: 'record',
        title: 'Record',
        tech: 'Solana devnet',
        text: 'In the sandbox, a Solana devnet program verifies and records the bet.',
        sample: 'devnet · 5KqT9w…x3Pz'
    }
]

export const ONBOARDING_STEPS: TitledText[] = [
    {
        title: 'Create your casino',
        text: 'Name it, add your website and, if you like, invite a developer.'
    },
    {
        title: 'Accept your theme',
        text: "We read your site's colours, fonts and logo. Accept the theme or adjust it."
    },
    {
        title: 'Deploy your game server',
        text: 'Connect Cloudflare and deploy the Worker to workers.dev or your own hostname.'
    },
    {
        title: 'Embed the games',
        text: "Add one script and one tag, or hand the dashboard's instructions to your coding agent."
    },
    {
        title: 'Go live',
        text: 'Add your wallet address and pay the first quarter in USDC, USDT or SOL.'
    }
]

export const EMBED_SNIPPET = `<og-game game="dice" controls></og-game>

<script type="module">
  import { connect } from "https://og-rgs-lucky-star.luckystar.workers.dev/element/v1/loader.js"

  connect(document.querySelector("og-game"), {
    token: async ({ publicKey }) => {
      const response = await fetch("/api/game-session", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ publicKey })
      })

      return response.text()
    }
  })
</script>`

export const SESSION_SNIPPET = `import { createHmac } from "node:crypto"

const encode = (value) => Buffer.from(JSON.stringify(value)).toString("base64url")

// Runs on your backend, behind your own login
export function sessionToken({ player, currency, publicKey }) {
  const header = encode({ alg: "HS256", typ: "JWT" })
  const claims = encode({
    sub: player,
    tenant: "lucky-star",
    iss: "https://luckystar.example",
    aud: "og-rgs-v1",
    currency,
    exp: Math.floor(Date.now() / 1000) + 900,
    ...(publicKey && { cnf: { jwk: { kty: "OKP", crv: "Ed25519", x: publicKey } } })
  })

  const signature = createHmac("sha256", process.env.SESSION_SECRET)
    .update(header + "." + claims)
    .digest("base64url")

  return header + "." + claims + "." + signature
}`

export const PRICING_TIERS: PricingTier[] = [
    { label: 'Under $250K', short: '<250K', monthly: 263, quarterly: 789 },
    { label: '$250K to $500K', short: '250K', monthly: 604, quarterly: 1812 },
    { label: '$500K to $1M', short: '500K', monthly: 1136, quarterly: 3408 },
    { label: '$1M to $2.5M', short: '1M', monthly: 2134, quarterly: 6402 },
    { label: '$2.5M to $5M', short: '2.5M', monthly: 4912, quarterly: 14736 },
    { label: '$5M to $10M', short: '5M', monthly: 9230, quarterly: 27690 },
    { label: '$10M and up', short: '10M+', monthly: 17343, quarterly: 52029 }
]

export const PRICING_NOTES = [
    'The sandbox is free and mirrors live mode with test credits',
    'Solana recording fees are included',
    'No revenue share and no cap on bets',
    'Months of a paid quarter that have not started are refunded'
]

export const FAQ: FaqItem[] = [
    {
        question: 'Do we need our own Cloudflare account?',
        answer: 'Yes. The Worker, its Durable Objects and its secrets all live in your account, so the game server is yours.'
    },
    {
        question: 'How do the games reach our wallet?',
        answer: "The game server calls three signed endpoints on your backend: debit, credit and balance. Every credit carries the round's proof, so your wallet can check it before it pays."
    },
    {
        question: 'Who holds the key that decides outcomes?',
        answer: 'Your game server creates its own VRF key and never exports it. Kinematic holds no key that decides an outcome, and the server cannot see an outcome before it accepts the bet.'
    },
    {
        question: 'Are bets recorded on Solana?',
        answer: 'In the sandbox, a Solana devnet program verifies and records every settled bet. Live mode records nothing on Solana until the audited program is on mainnet. Players can check bets in the browser in both modes.'
    },
    {
        question: 'What can we change?',
        answer: "Which games you offer, your currencies and limits, the return to player (99, 98, 97 or 96%), the colours, fonts and corner radius, and the element's tag name. Layout and motion stay fixed."
    },
    {
        question: 'Which currencies can players use?',
        answer: 'As many as you add. Each currency has its own decimal places, stake limits and payout cap.'
    },
    {
        question: 'How does billing work?',
        answer: 'A flat fee by tier, set by the monthly deposits you declare at sign-up and at each renewal. You pay each quarter upfront in USDC, USDT or SOL, with no revenue share.'
    }
]
